import { Button, Col, Row, Table, Typography } from "antd";
import { ReusableModal } from "@/components/UI/Modals/CustomModals";
import { useMemo, useState } from "react";
import { useGetOrderByReferenceQuery } from "@/services/api/orders/ordersApi";
import { formatCurrency } from "@/utils/helper";

const { Title } = Typography;
const TransactionDetailsModal = ({ open, onClose, transactionDetails }) => {
  const {
    data: orderDetailsByRef,
    isLoading: orderDetailsLoading,
    // isFetching,
  } = useGetOrderByReferenceQuery(transactionDetails?.order_reference);
  // API returns { status, message, data: [order] }, extract first order from data array
  const orderDetails = orderDetailsByRef?.[0] || null;

  const [activeTab, setActiveTab] = useState(0);
  const tabs = [
    {
      label: "Transaction Details",
    },
    {
      label: "Order Details",
    },
  ];

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
  };
  const InfoRow = ({ label, value, leftSide = true }) => (
    <div className={leftSide ? "mb-6" : "mb-6"}>
      <p className="text-sm text-gray-600 mb-2">{label}</p>
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-2">
        <p className="text-gray-900 font-medium my-auto">{value || "N/A"}</p>
      </div>
    </div>
  );

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Helper function to format time
  const formatTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const renderTabContent = () => {
    // Guard clause - if no transaction details, show placeholder
    if (!transactionDetails) {
      return (
        <div className="text-center py-8 text-gray-500">
          No transaction details available
        </div>
      );
    }
    // Sanitize the amount string by removing commas before parsing
    const cleanedAmountString = transactionDetails.amount
      ? transactionDetails.amount.replace(/,/g, "") // Remove all commas
      : "0"; // Default to '0' if amount is null/undefined
    const OrderDetailsTable = ({ orderDetails: orderData }) => {
      const columns = [
        {
          title: "S/N",
          dataIndex: "sn",
          key: "sn",
          render: (text, record, index) => index + 1, // Auto-generate S/N
        },
        {
          title: "Product Name",
          dataIndex: "productName",
          key: "productName",
        },
        {
          title: "Quantity",
          dataIndex: "quantity",
          key: "quantity",
        },
        {
          title: "Amount",
          dataIndex: "amount",
          key: "amount",
          align: "right", // Align amount to the right
          render: (text) => formatCurrency(text), // Format to currency
        },
      ];

      const defaultOrderItems = useMemo(() => {
        if (!orderData?.items || !Array.isArray(orderData.items)) {
          return [];
        }
        return orderData.items.map((item) => ({
          id: item.id,
          productName: item.productable?.name || "Unknown Product",
          amount: item.amount || "0.00",
          quantity: item.qty || 1,
        }));
      }, [orderData]);

      const dataSource = defaultOrderItems;

      return (
        <div className="border-1 border-gray-200 rounded-lg overflow-hidden">
          <Title
            level={5}
            className="p-4  font-medium border-b border-gray-200"
          >
            Order Details{" "}
          </Title>
          <Table
            columns={columns}
            dataSource={dataSource}
            rowKey="id" // Important for table performance and selection
            pagination={false} // No pagination needed for a simple list
            className="custom-table"
            loading={orderDetailsLoading}
          />
        </div>
      );
    };

    const transactionAmount = parseFloat(cleanedAmountString);

    const serviceFee = transactionAmount ? transactionAmount * 0.05 : 0;
    switch (activeTab) {
      case 0:
        return (
          <Row gutter={[24, 24]}>
            <Col xs={24} lg={12}>
              <InfoRow
                label="Transaction Reference"
                value={transactionDetails.id}
              />
              <InfoRow
                label="Total Amount Paid by Customer"
                value={`₦${transactionDetails.amount}`}
              />
              <InfoRow
                label="Date"
                value={formatDate(transactionDetails.created_at)}
              />
              <InfoRow label="Status" value={transactionDetails.status} />
            </Col>
            <Col xs={24} lg={12}>
              <InfoRow
                label="Payment Gateway"
                value={transactionDetails.platform}
                leftSide={false}
              />
              <InfoRow
                label="Service Fee"
                value={serviceFee ? `₦${serviceFee.toFixed(2)}` : "N/A"}
                leftSide={false}
              />
              <InfoRow
                label="Time"
                value={formatTime(transactionDetails.created_at)}
                leftSide={false}
              />
              <InfoRow
                label="Order Type"
                value={transactionDetails.order_type_name}
                leftSide={false}
              />
            </Col>
          </Row>
        );
      case 1:
        return (
          <>
            <Row gutter={[24, 24]}>
              <Col xs={24} lg={12}>
                <InfoRow
                  label="Order ID"
                  value={transactionDetails?.order_reference}
                />
              </Col>
              <Col xs={24} lg={12}>
                <InfoRow
                  label="Total Order Amount"
                  value={formatCurrency(transactionDetails?.amount)}
                />
              </Col>
            </Row>
            <Row gutter={[24, 24]}>
              <Col xs={24} lg={24}>
                {" "}
                <OrderDetailsTable orderDetails={orderDetails} />
              </Col>
            </Row>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <ReusableModal
      open={open}
      onClose={onClose}
      title="Transaction Log"
      subtitle="Kindly see transaction details below"
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={handleTabChange}
      customFooter={true}
      position="right"
      slideDirection="right"
      animationDuration={400}
      width={600}
      footer={
        <div className="flex justify-end">
          <Button onClick={onClose}>Close</Button>
        </div>
      }
    >
      {renderTabContent()}
    </ReusableModal>
  );
};

export default TransactionDetailsModal;
